import { useState, useCallback, useMemo } from 'react';

// The names the discard prompts use, which must read as the section headings
// do. `person` and `familyNote` were missing, so the save footer's discard
// dialog (and the tab-close prompt that reuses it) fell back to the raw key
// and told a clinician "Unsaved changes to person will be lost"
// (TGCLOUD-1839). useDirtyState.test.js checks every key the sidebar marks
// has an entry here.
const SECTION_LABELS = {
  person: 'Person',
  diagnoses: 'Diseases',
  phenotypes: 'Phenotypes',
  genotypes: 'Genotypes',
  tags: 'Tags',
  externalFamilyIds: 'External IDs',
  familyNote: 'Family Note'
};

const INITIAL = {
  diagnoses: false,
  phenotypes: false,
  genotypes: false,
  tags: false,
  externalFamilyIds: false
};

export function useDirtyState() {
  const [dirty, setDirty] = useState(INITIAL);
  // How many fields of each section hold an unsaved change, for the sections
  // that compare their values (TGCLOUD-1902): the section's marker, and the save
  // footer's "N unsaved changes". A section that reports no count is dirty or
  // clean as before, and counts as one change.
  const [changeCounts, setChangeCounts] = useState({});

  const markDirty = useCallback((section) => {
    setDirty((prev) => (prev[section] ? prev : { ...prev, [section]: true }));
  }, []);

  const markClean = useCallback((section) => {
    setDirty((prev) => (prev[section] ? { ...prev, [section]: false } : prev));
  }, []);

  const setChangeCount = useCallback((section, count) => {
    const next = Number.isFinite(count) && count > 0 ? count : 0;
    setChangeCounts((prev) =>
      (prev[section] ?? 0) === next ? prev : { ...prev, [section]: next }
    );
  }, []);

  const markAllClean = useCallback(() => {
    setDirty(INITIAL);
    setChangeCounts({});
  }, []);

  const isDirty = useCallback((section) => dirty[section] ?? false, [dirty]);

  const changeCountOf = useCallback(
    (section) => (dirty[section] ? Math.max(changeCounts[section] ?? 0, 1) : 0),
    [dirty, changeCounts]
  );

  const dirtyCount = useMemo(
    () => Object.values(dirty).filter(Boolean).length,
    [dirty]
  );

  const changeCount = useMemo(
    () =>
      Object.keys(dirty)
        .filter((section) => dirty[section])
        .reduce(
          (sum, section) => sum + Math.max(changeCounts[section] ?? 0, 1),
          0
        ),
    [dirty, changeCounts]
  );

  const dirtySections = useMemo(
    () =>
      Object.entries(dirty)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [dirty]
  );

  const dirtySectionLabels = useMemo(
    () => dirtySections.map((k) => SECTION_LABELS[k] ?? k),
    [dirtySections]
  );

  return {
    markDirty,
    markClean,
    markAllClean,
    isDirty,
    dirtyCount,
    dirtySections,
    dirtySectionLabels,
    setChangeCount,
    changeCountOf,
    changeCount
  };
}
